import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Star, X, Gift, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackForm = () => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [otherProductName, setOtherProductName] = useState('');
  const [otherProductError, setOtherProductError] = useState('');
  const [formData, setFormData] = useState({
    liked: '',
    improvements: '',
    name: '',
    email: '',
    mobile: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [showCouponPopup, setShowCouponPopup] = useState(false);

  const products = [
    'Ragi Bhujia - Achari Flavour',
    'Baked Multigrain Bhujia - Pudina Flavour',
    'Jowar & Jeera Cookies',
    'Other Product'
  ];

  const handleProductToggle = (product) => {
    setSelectedProducts(prev => {
      if (prev.includes(product)) {
        // If unchecking "Other Product", clear the text input and error
        if (product === 'Other Product') {
          setOtherProductName('');
          setOtherProductError('');
        }
        return prev.filter(p => p !== product);
      } else {
        return [...prev, product];
      }
    });
  };

  const validateOtherProductName = (value) => {
    // Only allow letters and spaces (no numbers or special characters)
    const letterOnlyRegex = /^[A-Za-z\s]+$/;
    
    if (!value.trim()) {
      setOtherProductError('Product name is required');
      return false;
    } else if (!letterOnlyRegex.test(value)) {
      setOtherProductError('Product name must contain only letters (no numbers or special characters)');
      return false;
    } else {
      setOtherProductError('');
      return true;
    }
  };

  const handleOtherProductChange = (e) => {
    const value = e.target.value;
    setOtherProductName(value);
    
    // Validate on change
    if (value) {
      validateOtherProductName(value);
    } else {
      setOtherProductError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    // Validate "Other Product" if selected
    if (selectedProducts.includes('Other Product')) {
      if (!validateOtherProductName(otherProductName)) {
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Prepare products array with "Other Product" name if provided
      const productsToSubmit = selectedProducts.map(product => {
        if (product === 'Other Product' && otherProductName) {
          return `Other: ${otherProductName}`;
        }
        return product;
      });

      await axios.post(`${API}/feedback`, {
        ...formData,
        products: productsToSubmit,
        rating
      });
      
      // Show coupon popup
      setShowCouponPopup(true);
      
      // Reset form
      setFormData({
        liked: '',
        improvements: '',
        name: '',
        email: '',
        mobile: ''
      });
      setSelectedProducts([]);
      setOtherProductName('');
      setOtherProductError('');
      setRating(0);

    } catch (error) {
      setMessage('❌ Oops! Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeCouponPopup = () => {
    setShowCouponPopup(false);
  };

  const copyCouponCode = () => {
    navigator.clipboard.writeText('THANKS10');
    alert('Coupon code copied! 🎉');
  };

  return (
    <>
      {/* Thank You Coupon Popup */}
      {showCouponPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in-95 duration-500">
            <button
              onClick={closeCouponPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Animated confetti effect */}
            <div className="text-center mb-6">
              <div className="relative inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-red-400 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                  <Gift className="w-12 h-12 text-white" />
                </div>
                <Sparkles className="w-8 h-8 text-yellow-400 absolute -top-2 -right-2 animate-pulse" />
                <Sparkles className="w-6 h-6 text-orange-400 absolute -bottom-1 -left-1 animate-pulse delay-100" />
              </div>
              
              <h3 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Fredoka, sans-serif'}}>
                Thank You! 🎉
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Your feedback means the world to us!
              </p>
            </div>

            <div className="bg-gradient-to-r from-orange-100 to-yellow-100 rounded-2xl p-6 mb-6 border-2 border-orange-300 border-dashed">
              <p className="text-sm text-gray-700 mb-2 text-center font-semibold">
                Here's a special gift for you! 🎁
              </p>
              <div className="bg-white rounded-xl p-4 text-center">
                <p className="text-sm text-gray-600 mb-2">Use this coupon code:</p>
                <div className="text-3xl font-bold text-orange-500 tracking-wider mb-3" style={{fontFamily: 'Fredoka, sans-serif'}}>
                  THANKS10
                </div>
                <p className="text-xs text-gray-500 mb-3">Get 10% off your next order!</p>
                <Button
                  onClick={copyCouponCode}
                  className="bg-orange-500 hover:bg-orange-600 text-white w-full"
                >
                  Copy Code 📋
                </Button>
              </div>
            </div>

            <Button
              onClick={closeCouponPopup}
              variant="outline"
              className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-b from-orange-50 to-white py-20 px-4">
        <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-12">
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Fredoka, sans-serif'}}>
            We Want to Hear From You! 💬
          </h3>
          <p className="text-xl text-gray-600">
            Your feedback helps us create better snacks for you. Tell us what you think!
          </p>
        </div>

        <Card className="shadow-2xl border-2 border-orange-100">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Selection - Multiple */}
              <div>
                <Label className="text-gray-700 font-semibold text-lg mb-3 block">
                  Which products did you try? * (Select all that apply)
                </Label>
                <div className="space-y-3">
                  {products.map((product) => (
                    <div key={product} className="flex items-center space-x-3">
                      <Checkbox
                        id={product}
                        checked={selectedProducts.includes(product)}
                        onCheckedChange={() => handleProductToggle(product)}
                        className="border-2 border-orange-300"
                      />
                      <Label 
                        htmlFor={product} 
                        className="text-gray-700 cursor-pointer font-medium"
                      >
                        {product}
                      </Label>
                    </div>
                  ))}
                </div>
                
                {/* Conditional "Other Product" text input */}
                {selectedProducts.includes('Other Product') && (
                  <div className="mt-4 ml-7">
                    <Input
                      type="text"
                      placeholder="Please specify the product name (letters only)"
                      value={otherProductName}
                      onChange={handleOtherProductChange}
                      required
                      className={`border-orange-200 focus:border-orange-400 ${otherProductError ? 'border-red-500' : ''}`}
                    />
                    {otherProductError && (
                      <p className="text-red-500 text-sm mt-1">{otherProductError}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Rating */}
              <div>
                <Label className="text-gray-700 font-semibold text-lg mb-2 block">
                  How would you rate it? *
                </Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= (hoveredRating || rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="ml-3 text-gray-600 self-center">
                      {rating === 5 && '🤩 Amazing!'}
                      {rating === 4 && '😊 Great!'}
                      {rating === 3 && '👍 Good!'}
                      {rating === 2 && '😐 Okay'}
                      {rating === 1 && '😕 Needs work'}
                    </span>
                  )}
                </div>
              </div>

              {/* What they liked */}
              <div>
                <Label htmlFor="liked" className="text-gray-700 font-semibold text-lg">
                  What did you like about it? *
                </Label>
                <Textarea
                  id="liked"
                  placeholder="Tell us what made you smile... the taste, texture, packaging?"
                  value={formData.liked}
                  onChange={(e) => setFormData({ ...formData, liked: e.target.value })}
                  required
                  rows={4}
                  className="mt-2"
                />
              </div>

              {/* Improvements */}
              <div>
                <Label htmlFor="improvements" className="text-gray-700 font-semibold text-lg">
                  What can we improve?
                </Label>
                <Textarea
                  id="improvements"
                  placeholder="Any suggestions to make it even better? We're all ears!"
                  value={formData.improvements}
                  onChange={(e) => setFormData({ ...formData, improvements: e.target.value })}
                  rows={4}
                  className="mt-2"
                />
              </div>

              {/* Optional contact info */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-semibold">
                    Your Name (Optional)
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-700 font-semibold">
                    Your Email (Optional)
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile" className="text-gray-700 font-semibold">
                    Mobile Number (Optional)
                  </Label>
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="9876543210"
                    pattern="[0-9]{10}"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className="mt-2"
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Success/Error Message */}
              {message && (
                <div className={`text-center p-4 rounded-lg font-semibold ${
                  message.includes('Thank you') 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {message}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || rating === 0 || selectedProducts.length === 0}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-bold disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Submit Feedback 🚀'}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                * Required fields
              </p>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    </>
  );
};

export default FeedbackForm;
